import { SubscriptionLike as ISubscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ProcessParticipationService } from './../../../../services/process.participation.service';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { ProcessParticipationAnswer } from './../../../../models/process-models';
import { Component, OnInit, Input, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, Output, EventEmitter } from '@angular/core';
import { SurveyInfrastructure, QuestionType, Answer } from '../../../../models/questions';

@Component({
  selector: 'app-checkbox-radio-form',
  templateUrl: './checkbox-radio-form.component.html',
  styleUrls: ['../user-question-form-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckboxRadioFormComponent implements OnInit, OnDestroy {
  @Input() question: SurveyInfrastructure;
  @Output() questionAnswered = new EventEmitter<SurveyInfrastructure>();
  
  checkboxForm: FormGroup;

  private errorHeadMessage: string;
  private errorBodyMessage: string;
  private subscription: ISubscription;
  private readonly translatorKeyErrorHead = 'error.updateAnswer.head';
  private readonly translatorKeyErrorBody = 'error.updateAnswer.body';

  constructor(private fb: FormBuilder, 
    private processParticipationservice: ProcessParticipationService, 
    private ref: ChangeDetectorRef,
    private toastr: ToastrService, private readonly translatorService: TranslateService) { }

  ngOnInit() {
    this.sortQuestionByOrder(this.question.answers);
    this.checkboxForm = this.fb.group({
      answers: this.buildAnswers()
    });
    this.subscription = this.translatorService.get([this.translatorKeyErrorHead, this.translatorKeyErrorBody])
    .subscribe(x => { 
      this.errorBodyMessage = x[this.translatorKeyErrorBody];
      this.errorHeadMessage = x[this.translatorKeyErrorHead];
    });
    }

    ngOnDestroy() {
      this.subscription.unsubscribe();
    }

  buildAnswers() {
    const answersControl = this.question.answers.map(a => {
      a.selected = this.question.processParticipationAnswers.some(x => x.answerId === a.id);
      const formValue = (a.selected) ? a.selected : false;
      return this.fb.control(formValue);
    });
    return this.fb.array(answersControl);
  }

  getAnswerrControls() {
    const answersControls = this.checkboxForm.get('answers');
    return answersControls['controls'];
  }

  getIsCheckbox(): boolean {
    return this.question.answerType === QuestionType.Checkbox;
  }

  getIsRating(): boolean {
    return this.question.answerType === QuestionType.Rating;
  }

  selectAnswer(answer: Answer) {
    let processParticipationAnswer: ProcessParticipationAnswer;
    if (!this.getIsCheckbox()) {
      const rbAnswer = [...this.question.processParticipationAnswers].shift();
      processParticipationAnswer = this.changeRadiobuttonAnswer(rbAnswer, answer.id);
    } else {
      processParticipationAnswer = this.changeCheckboxAnswer(answer);
      if (!processParticipationAnswer) {
        return;
      }
    }
    this.addOrUpdateAnswer(processParticipationAnswer);
  }

  getUniqueInputName(questionAnswerId: number) {
    return this.question.id.toString() + questionAnswerId.toString();
  }

  private addOrUpdateAnswer(answer: ProcessParticipationAnswer) {
    this.updateAnswersControl();
    this.processParticipationservice.addOrUpdateAnswer(this.question.processParticipationId, answer)
      .subscribe(x => {
        this.question.processParticipationAnswers = x;
        this.questionAnswered.emit(this.question);
        this.updateAnswersControl();
        this.ref.detectChanges();
      }, 
        err => this.toastr.error(this.errorBodyMessage, this.errorHeadMessage));
        
  }

  private changeRadiobuttonAnswer(answer: ProcessParticipationAnswer, answerId: number): ProcessParticipationAnswer {
    if (answer) {
      answer.answerId = answerId;
    } else {
      answer = this.createAnswer(answerId);
    }
    return answer;
  }

  private changeCheckboxAnswer(answer: Answer): ProcessParticipationAnswer {
    let processParticipationAnswer = {};
    const cbAnswer = this.question.processParticipationAnswers.find(x => x.answerId === answer.id);
    if (cbAnswer) {
      this.deleteAnswer(cbAnswer);
      return;
    } else {
      processParticipationAnswer = this.createAnswer(answer.id);
    }
    this.ref.detectChanges();
    return processParticipationAnswer;
  }

  private deleteAnswer(processParticipationAnswer: ProcessParticipationAnswer) {
    this.updateAnswersControl();
    this.processParticipationservice.deleteAnswer(this.question.processParticipationId, processParticipationAnswer)
      .subscribe(x => {
        this.question.processParticipationAnswers = x;
        this.updateAnswersControl();
      },
         err => this.toastr.error(this.errorBodyMessage, this.errorHeadMessage));
  }

  private updateAnswersControl() {
    this.checkboxForm = this.fb.group({
      answers: this.buildAnswers()
    });
    this.ref.detectChanges();
  }

  private createAnswer(answerId: number): ProcessParticipationAnswer {
    return {
      processParticipationId: this.question.processParticipationId,
      answerId: answerId,
      surveyInfrastructureId: this.question.id
    };
  }

  private sortQuestionByOrder(surveyInfrastructures: Array<SurveyInfrastructure>) {
    surveyInfrastructures.sort((a, b) => a.order - b.order);
  }
}
